import { AppHeader, FlexContainer, LoadingPage, ReemaThemeV2 } from '@act/storybook';
import { ThemeProvider } from '@mui/material/styles';
import { ConsentAppRouter } from '../consents/member-view/ConsentAppRouter';
import { Provider } from 'react-redux';
import { getStore } from 'redux/consentStore.util';
import { useGetAppConfig } from 'utilities/appConfigHook.hook';
import AppConfigLoadError from './AppConfigLoadError';
import { LanguageProvider } from 'utilities/language-provider';
import ConsentActionsInitializer from '../consents/member-view/ConsentActionsInitializer';
import { TrackingProvider } from '../tracking/TrackingContext';

// Initialize i18n
import '../../utilities/i18n';

import { useSearchParams, useParams } from 'react-router-dom';

/**
 * Consent member shell shared across loading, error, and ready states.
 */
const ConsentAppShell = ({
  termKey,
  consentKey,
}: {
  termKey?: string;
  consentKey: string | null;
}) => (
  <ThemeProvider theme={ReemaThemeV2.LIGHT_MODE}>
    <FlexContainer
      id="consent-app"
      direction="column"
      height="100vh"
      width="100%"
      gap={0}
      overflow="hidden"
    >
      <Provider store={getStore()}>
        <ConsentActionsInitializer />
        <LanguageProvider>
          <AppHeader
            href={
              consentKey
                ? `/?key=${encodeURIComponent(consentKey)}`
                : '/'
            }
            linkLogo={Boolean(consentKey)}
            withLogo
            disableAnimations={true}
            disableMobileMenu={true}
          />
          <ConsentAppRouter termKey={termKey} />
        </LanguageProvider>
      </Provider>
    </FlexContainer>
  </ThemeProvider>
);

const ConsentAppContent = () => {
  const appConfig = useGetAppConfig();
  const [params] = useSearchParams();
  const { termKey } = useParams<{ termKey?: string }>();
  const key = params.get('key');
  const hasConsentKey = Boolean(key);

  // Missing key shows the enrollment-link error without waiting on client-config.
  // Omit termKey so ConsentAppRouter mounts ConsentsHome (error UI) even on /:termKey routes.
  if (!hasConsentKey) {
    return <ConsentAppShell consentKey={key} />;
  }

  if (appConfig.status === 'loading') {
    return (
      <ThemeProvider theme={ReemaThemeV2.LIGHT_MODE}>
        <LoadingPage></LoadingPage>
      </ThemeProvider>
    );
  }

  if (appConfig.status === 'error') {
    return <AppConfigLoadError message={appConfig.errorMessage} />;
  }

  return <ConsentAppShell termKey={termKey} consentKey={key} />;
};

/**
 * Consent member shell with an app-wide tracking context.
 * @returns Consent application root.
 */
const ConsentApp = () => (
  <TrackingProvider contextName="consent-app">
    <ConsentAppContent />
  </TrackingProvider>
);

export default ConsentApp;
